'use client';

import {
    type CSSProperties,
    type ReactElement,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { cn } from '@/lib/utils';
import { sanitizeHtml } from '@/lib/sanitize-html';

export type TruncationConfig = { mode: 'rows'; value: number } | { mode: 'chars'; value: number };

export type InlineTruncationContentType = 'plain' | 'html';

export interface InlineTruncationTextProps {
    /** Plain-text content (legacy alias for {@link content}). */
    text?: string;
    /** Full plain-text or HTML string to truncate. */
    content?: string;
    /** `plain` uses inline word truncation for row limits; `html` always clips in the DOM. */
    contentType?: InlineTruncationContentType;
    /** Explicit truncation mode — rows or characters. */
    truncation?: TruncationConfig;
    /** Legacy row limit for plain text (inline read-more placement). */
    collapsedRows?: number;
    /** Character limit; keeps the full content in the DOM and clips visually. */
    collapsedChars?: number;
    /** The "Read More" string (used to reserve exact space at the end of the last line). */
    readMoreLabel: string;
    /** The "Read Less" string shown when the text is expanded. */
    readLessLabel: string;
    /** Default line height if the browser returns 'normal' (default: 24). */
    fallbackLineHeightPx?: number;
    /** Wrapper element classes. */
    wrapperClassName?: string;
    /** Text element classes. */
    textClassName?: string;
    /** Read more button classes. */
    readMoreClassName?: string;
    /** Read less button classes. */
    readLessClassName?: string;
}

interface SharedTruncationStyleProps {
    readMoreLabel: string;
    readLessLabel: string;
    fallbackLineHeightPx: number;
    wrapperClassName?: string;
    textClassName?: string;
    readMoreClassName?: string;
    readLessClassName?: string;
}

interface PlainRowsInlineTruncationProps extends SharedTruncationStyleProps {
    text: string;
    collapsedRows: number;
}

interface ClippedContentTruncationProps extends SharedTruncationStyleProps {
    content: string;
    contentType: InlineTruncationContentType;
    truncation: TruncationConfig;
}

/**
 * Binary search algorithm to find the maximum number of words that fit within a container's height.
 */
function maxWordsThatFit(wordCount: number, fits: (wordIndex: number) => boolean): number {
    let low = 0;
    let high = wordCount;
    let best = 0;
    while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        if (fits(mid)) {
            best = mid;
            low = mid + 1;
        } else {
            high = mid - 1;
        }
    }
    return best;
}

function splitWords(text: string): string[] {
    return text.trim().split(/\s+/u).filter(Boolean);
}

function collapsedPlainText(words: string[], wordCount: number, readMoreLabel: string): string {
    if (wordCount <= 0) return readMoreLabel;
    return `${words.slice(0, wordCount).join(' ')} ${readMoreLabel}`.trim();
}

function clampStyle(collapsedRows: number): CSSProperties {
    return {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: collapsedRows,
        overflow: 'hidden',
    };
}

const INLINE_TEXT_STYLE: CSSProperties = {
    display: 'inline',
};

function getPlainTextFromHtml(html: string): string {
    return html
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function truncatePlainTextAtWord(text: string, maxLength: number): string {
    if (text.length <= maxLength) {
        return text;
    }

    const truncated = text.slice(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');
    return (lastSpace > 0 ? truncated.slice(0, lastSpace) : truncated).trim();
}

function resolveContent(props: InlineTruncationTextProps): string {
    return (props.content ?? props.text ?? '').trim();
}

function resolveTruncation(props: InlineTruncationTextProps): TruncationConfig {
    if (props.truncation != null) {
        return props.truncation;
    }

    if (props.collapsedChars != null) {
        return { mode: 'chars', value: props.collapsedChars };
    }

    if (props.collapsedRows != null) {
        return { mode: 'rows', value: props.collapsedRows };
    }

    return { mode: 'rows', value: 2 };
}

function usesPlainRowsInlineStrategy(contentType: InlineTruncationContentType, truncation: TruncationConfig): boolean {
    return contentType === 'plain' && truncation.mode === 'rows';
}

/**
 * Resolves the computed line-height of an element in pixels.
 * Falls back to a dummy 'M' span measurement if CSS returns 'normal'.
 */
function getLineHeightPx(element: HTMLElement, fallback: number): number {
    const raw = window.getComputedStyle(element).lineHeight;
    const parsed = Number.parseFloat(raw);
    if (Number.isFinite(parsed)) return parsed;

    const probe = document.createElement('span');
    probe.textContent = 'M';
    probe.style.position = 'absolute';
    probe.style.visibility = 'hidden';
    probe.style.whiteSpace = 'nowrap';
    element.appendChild(probe);
    const h = probe.getBoundingClientRect().height;
    probe.remove();
    return h > 0 ? h : fallback;
}

/**
 * Creates an invisible clone of the given element with the given width for measuring purposes.
 */
function createClonedElement(element: HTMLElement, width: number): HTMLElement {
    const measure = document.createElement('div');
    measure.setAttribute('aria-hidden', 'true');
    measure.className = element.className;
    measure.lang = element.lang;
    measure.style.cssText = [
        'position:absolute',
        'left:0',
        'top:0',
        'visibility:hidden',
        'pointer-events:none',
        `width:${width}px`,
        'box-sizing:border-box',
        'margin:0',
        'max-height:none',
        'overflow:visible',
        'white-space:normal',
        'word-break:normal',
        'overflow-wrap:anywhere',
    ].join(';');

    return measure;
}

function setProbeContent(probe: HTMLElement, content: string, contentType: InlineTruncationContentType): void {
    if (contentType === 'html') {
        probe.innerHTML = content;
        return;
    }

    probe.textContent = content;
}

/**
 * Plain text truncated by row count with an inline read-more control at the end of the last line.
 */
function PlainRowsInlineTruncation({
    text,
    readMoreLabel,
    readLessLabel,
    collapsedRows,
    fallbackLineHeightPx,
    wrapperClassName,
    textClassName,
    readMoreClassName,
    readLessClassName,
}: PlainRowsInlineTruncationProps): ReactElement | null {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const textRef = useRef<HTMLParagraphElement | null>(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [collapsedPrefix, setCollapsedPrefix] = useState(text);
    const [canExpand, setCanExpand] = useState(false);
    const [hasMeasured, setHasMeasured] = useState(false);

    const lastMeasuredWidth = useRef<number>(0);
    const lastMeasuredText = useRef<string>('');
    const resizeFrameIdRef = useRef<number | null>(null);
    const lastObservedWidth = useRef<number | null>(null);

    const measureAndTruncate = useCallback(() => {
        const visible = containerRef.current;
        const textElement = textRef.current;
        if (visible == null || textElement == null || isExpanded) return;

        const currentWidth = visible.clientWidth;
        const currentText = text;

        if (hasMeasured && currentWidth === lastMeasuredWidth.current && currentText === lastMeasuredText.current) {
            return;
        }

        if (currentWidth <= 0) return;

        const words = splitWords(currentText);
        if (words.length === 0) {
            setCollapsedPrefix('');
            setCanExpand(false);
            setHasMeasured(true);
            return;
        }

        const measure = createClonedElement(textElement, currentWidth);
        (visible.parentElement ?? document.body).appendChild(measure);

        try {
            const lineHeight = getLineHeightPx(textElement, fallbackLineHeightPx);
            const maxHeight = lineHeight * collapsedRows;

            measure.textContent = currentText;
            const fullFits = measure.scrollHeight <= maxHeight + 0.5;

            if (fullFits) {
                setCollapsedPrefix(currentText);
                setCanExpand(false);
                setHasMeasured(true);
                lastMeasuredWidth.current = currentWidth;
                lastMeasuredText.current = currentText;
                return;
            }

            setCanExpand(true);

            const best = maxWordsThatFit(words.length, (mid) => {
                measure.textContent = collapsedPlainText(words, mid, readMoreLabel);
                return measure.scrollHeight <= maxHeight + 1;
            });

            setCollapsedPrefix(words.slice(0, best).join(' '));
            setHasMeasured(true);
            lastMeasuredWidth.current = currentWidth;
            lastMeasuredText.current = currentText;
        } finally {
            measure.remove();
        }
    }, [collapsedRows, fallbackLineHeightPx, hasMeasured, isExpanded, readMoreLabel, text]);

    useLayoutEffect(() => {
        setIsExpanded(false);
        setCollapsedPrefix(text);
        setCanExpand(false);
        setHasMeasured(false);
        lastMeasuredWidth.current = 0;
        lastMeasuredText.current = '';
        lastObservedWidth.current = null;
    }, [collapsedRows, readMoreLabel, text, textClassName]);

    useEffect(() => {
        const visible = containerRef.current;
        if (visible == null || isExpanded) return;

        const scheduleMeasurement = () => {
            if (resizeFrameIdRef.current !== null) {
                window.cancelAnimationFrame(resizeFrameIdRef.current);
            }

            resizeFrameIdRef.current = window.requestAnimationFrame(() => {
                measureAndTruncate();
                resizeFrameIdRef.current = null;
            });
        };

        const initialWidth = Math.round(visible.clientWidth);
        if (initialWidth > 0) {
            lastObservedWidth.current = initialWidth;
            scheduleMeasurement();
        }

        const resizeObserver = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (!entry) return;

            const nextWidth = Math.round(entry.contentRect.width);
            if (nextWidth <= 0 || nextWidth === lastObservedWidth.current) {
                return;
            }

            lastObservedWidth.current = nextWidth;
            scheduleMeasurement();
        });
        resizeObserver.observe(visible);

        return () => {
            resizeObserver.disconnect();

            if (resizeFrameIdRef.current !== null) {
                window.cancelAnimationFrame(resizeFrameIdRef.current);
                resizeFrameIdRef.current = null;
            }
        };
    }, [isExpanded, measureAndTruncate]);

    if (!text.trim()) {
        return null;
    }

    const showClampFallback = !hasMeasured && !isExpanded;
    const shouldRenderReadMoreButton = hasMeasured && canExpand && !isExpanded;
    const shouldRenderReadLessButton = hasMeasured && canExpand && isExpanded;
    const shouldShowFullText = isExpanded || !canExpand || showClampFallback;
    const displayText = shouldShowFullText ? text : collapsedPrefix;
    const shouldRenderInlineSpace = shouldRenderReadMoreButton && collapsedPrefix.length > 0;
    const textStyle = showClampFallback
        ? clampStyle(collapsedRows)
        : shouldRenderReadMoreButton
          ? INLINE_TEXT_STYLE
          : null;
    const textStyleProps = textStyle ? { style: textStyle } : undefined;

    return (
        <div className={wrapperClassName}>
            <div ref={containerRef} className="w-full">
                <p ref={textRef} className={textClassName} {...textStyleProps}>
                    {displayText}
                    {shouldRenderInlineSpace && ' '}
                </p>

                {shouldRenderReadMoreButton && (
                    <button
                        type="button"
                        onClick={() => setIsExpanded(true)}
                        className={readMoreClassName}
                        aria-expanded={false}>
                        {readMoreLabel}
                    </button>
                )}
            </div>

            {shouldRenderReadLessButton && (
                <button
                    type="button"
                    onClick={() => setIsExpanded(false)}
                    className={readLessClassName}
                    aria-expanded={true}>
                    {readLessLabel}
                </button>
            )}
        </div>
    );
}

/**
 * Keeps the full content in the DOM and clips it with max-height when collapsed (SEO-friendly).
 */
function ClippedContentTruncation({
    content,
    contentType,
    truncation,
    readMoreLabel,
    readLessLabel,
    fallbackLineHeightPx,
    wrapperClassName,
    textClassName,
    readMoreClassName,
    readLessClassName,
}: ClippedContentTruncationProps): ReactElement | null {
    const contentRef = useRef<HTMLDivElement | null>(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [collapsedMaxHeight, setCollapsedMaxHeight] = useState<number | undefined>(undefined);
    const [canExpand, setCanExpand] = useState(false);

    // Sanitized once and shared by the rendered markup and the measurement probe, which is attached to the DOM.
    const safeContent = useMemo(
        () => (contentType === 'html' ? sanitizeHtml(content) : content),
        [content, contentType]
    );
    const plainText = useMemo(
        () => (contentType === 'html' ? getPlainTextFromHtml(safeContent) : safeContent.trim()),
        [safeContent, contentType]
    );

    const isCollapsed = canExpand && !isExpanded;

    const measureCollapsedHeight = useCallback(() => {
        const contentElement = contentRef.current;
        if (contentElement == null || isExpanded) {
            return;
        }

        const width = contentElement.clientWidth;
        if (width <= 0) {
            return;
        }

        const measure = createClonedElement(contentElement, width);
        (contentElement.parentElement ?? document.body).appendChild(measure);

        try {
            if (truncation.mode === 'chars') {
                if (plainText.length <= truncation.value) {
                    setCanExpand(false);
                    setCollapsedMaxHeight(undefined);
                    return;
                }

                setCanExpand(true);
                measure.textContent = truncatePlainTextAtWord(plainText, truncation.value);
                setCollapsedMaxHeight(measure.scrollHeight);
                return;
            }

            const lineHeight = getLineHeightPx(contentElement, fallbackLineHeightPx);
            const maxHeight = lineHeight * truncation.value;
            setProbeContent(measure, safeContent, contentType);
            const fullHeight = measure.scrollHeight;

            if (fullHeight <= maxHeight + 0.5) {
                setCanExpand(false);
                setCollapsedMaxHeight(undefined);
                return;
            }

            setCanExpand(true);
            setCollapsedMaxHeight(maxHeight);
        } finally {
            measure.remove();
        }
    }, [safeContent, contentType, fallbackLineHeightPx, isExpanded, plainText, truncation]);

    useLayoutEffect(() => {
        setIsExpanded(false);
        setCanExpand(false);
        setCollapsedMaxHeight(undefined);
    }, [content, contentType, plainText, textClassName, truncation]);

    useLayoutEffect(() => {
        if (isExpanded) {
            return;
        }

        measureCollapsedHeight();
    }, [content, isExpanded, measureCollapsedHeight, truncation]);

    useLayoutEffect(() => {
        const contentElement = contentRef.current;
        if (contentElement == null || !isCollapsed) {
            return;
        }

        const resizeObserver = new ResizeObserver(() => {
            measureCollapsedHeight();
        });
        resizeObserver.observe(contentElement);

        return () => {
            resizeObserver.disconnect();
        };
    }, [isCollapsed, measureCollapsedHeight]);

    if (!content.trim()) {
        return null;
    }

    const collapsedStyle: CSSProperties | undefined =
        isCollapsed && collapsedMaxHeight != null ? { maxHeight: collapsedMaxHeight } : undefined;
    const toggleClassName = readMoreClassName ?? readLessClassName;

    return (
        <div className={wrapperClassName}>
            <div
                ref={contentRef}
                className={cn(textClassName, isCollapsed && 'overflow-hidden')}
                style={collapsedStyle}>
                {contentType === 'html' ? (
                    // eslint-disable-next-line react/no-danger
                    <div dangerouslySetInnerHTML={{ __html: safeContent }} />
                ) : (
                    <p>{content}</p>
                )}
            </div>

            {canExpand && toggleClassName != null && (
                <button
                    type="button"
                    onClick={() => setIsExpanded((prev) => !prev)}
                    aria-expanded={isExpanded}
                    className={isExpanded ? readLessClassName : readMoreClassName}>
                    {isExpanded ? readLessLabel : readMoreLabel}
                </button>
            )}
        </div>
    );
}

/**
 * Truncates long plain-text or HTML content with read more / read less controls.
 *
 * - `plain` + row limit: inline word truncation (legacy PLP behaviour).
 * - `html` or character limit: full content stays in the DOM, clipped via max-height.
 */
export default function InlineTruncationText(props: InlineTruncationTextProps): ReactElement | null {
    const {
        readMoreLabel,
        readLessLabel,
        fallbackLineHeightPx = 24,
        wrapperClassName,
        textClassName,
        readMoreClassName,
        readLessClassName,
        contentType = 'plain',
    } = props;

    const content = resolveContent(props);
    const truncation = resolveTruncation(props);

    const sharedProps: SharedTruncationStyleProps = {
        readMoreLabel,
        readLessLabel,
        fallbackLineHeightPx,
        wrapperClassName,
        textClassName,
        readMoreClassName,
        readLessClassName,
    };

    if (usesPlainRowsInlineStrategy(contentType, truncation)) {
        return <PlainRowsInlineTruncation {...sharedProps} text={content} collapsedRows={truncation.value} />;
    }

    return (
        <ClippedContentTruncation
            {...sharedProps}
            content={content}
            contentType={contentType}
            truncation={truncation}
        />
    );
}
